/**
 * A money field that speaks piastres to the app and pounds to the human.
 *
 * Keeps its own text state so a half-typed "2500." is not destroyed on every
 * keystroke, and reports null upward when the text is not a valid amount —
 * the caller shows an error rather than silently reading zero.
 */
import { useEffect, useState } from 'react';
import { formatEgp, parseEgpToPiastres } from '@shared/money';

export default function MoneyInput({
  valuePiastres,
  onChange,
  id,
  placeholder = '0',
  className = '',
  autoFocus = false,
}: {
  valuePiastres: number;
  onChange: (piastres: number | null) => void;
  id?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState(() =>
    valuePiastres ? formatEgp(valuePiastres, { showCurrency: false, hideZeroFraction: true }) : ''
  );

  // Re-sync when a quick-fill button changes the value from outside.
  useEffect(() => {
    const parsed = parseEgpToPiastres(text);
    if (parsed !== valuePiastres) {
      setText(
        valuePiastres
          ? formatEgp(valuePiastres, { showCurrency: false, hideZeroFraction: true })
          : ''
      );
    }
    // Intentionally keyed only on the incoming value: reacting to `text` here
    // would fight the user mid-keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuePiastres]);

  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoFocus={autoFocus}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          onChange(e.target.value.trim() === '' ? 0 : parseEgpToPiastres(e.target.value));
        }}
        onBlur={() => {
          const parsed = parseEgpToPiastres(text);
          if (parsed !== null) {
            setText(formatEgp(parsed, { showCurrency: false, hideZeroFraction: true }));
          }
        }}
        className="field tabular pr-16 text-right text-2xl font-bold"
      />
      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-white/40">
        EGP
      </span>
    </div>
  );
}
